/**
 * Picks settled data from one request to show while another loads. The zoom
 * helpers are copied from BucketedQueryManager's private ones so QueryStore
 * does not depend on BQM; the BQM copy goes away when BQM is deleted.
 */
import {type RunsDataQueryConfig} from '../../../containers/RunsDataLoader';
import {
  type BucketedHistorySpec,
  specIsStepAxis,
} from '../../../state/runs/types';
import * as Filter from '../../../util/filters';
import {type Filter as FilterNode} from '../../../util/filterTypes';
import type * as Run from '../../../util/runs';
import {type Key} from '../../../util/runTypes';
import {
  isGroupedQueryType,
  type RequestKeys,
} from '../bucketedData/queries/requestKey';
import {type Range} from '../common';
import {type BucketedData, type QueryStoreRequest} from './queryStore';

/** A request with settled data. */
export interface PlaceholderCandidate {
  data: BucketedData;
  keys: RequestKeys;
  request: QueryStoreRequest;
}

/** Step axes use minStep/maxStep; custom x axes use minX/maxX. */
function getSpecZoomBounds(
  spec: BucketedHistorySpec
): [number | undefined, number | undefined] {
  if (specIsStepAxis(spec)) {
    return [spec.minStep, spec.maxStep];
  }
  return [spec.minX, spec.maxX];
}

/** The query's zoom range, or null when its specs disagree. */
export function queryZoomRange(query: RunsDataQueryConfig): Range | null {
  const specs = query.bucketedHistorySpecs;
  if (!specs || specs.length === 0) {
    return null;
  }
  const [min, max] = getSpecZoomBounds(specs[0]);
  const allMatch = specs.every(spec => {
    const [specMin, specMax] = getSpecZoomBounds(spec);
    return specMin === min && specMax === max;
  });
  return allMatch ? {min: min ?? null, max: max ?? null} : null;
}

/**
 * Whether `inner`'s zoom is at least as constrained as `outer`'s on both
 * bounds and more constrained on at least one. An open bound counts as less
 * constrained than any finite one; two open bounds count as more constrained
 * so that two full-range queries contain each other.
 */
export function isZoomContained(
  inner: RunsDataQueryConfig,
  outer: RunsDataQueryConfig
): boolean {
  const innerRange = queryZoomRange(inner);
  const outerRange = queryZoomRange(outer);
  if (innerRange === null || outerRange === null) {
    return false;
  }

  const minIsMoreConstrained =
    outerRange.min === null ||
    (innerRange.min !== null && innerRange.min > outerRange.min);
  const minIsEqual =
    innerRange.min !== null &&
    outerRange.min !== null &&
    innerRange.min === outerRange.min;
  const maxIsMoreConstrained =
    outerRange.max === null ||
    (innerRange.max !== null && innerRange.max < outerRange.max);
  const maxIsEqual =
    innerRange.max !== null &&
    outerRange.max !== null &&
    innerRange.max === outerRange.max;

  return (
    (minIsMoreConstrained && maxIsMoreConstrained) ||
    (minIsMoreConstrained && maxIsEqual) ||
    (minIsEqual && maxIsMoreConstrained)
  );
}

/** Whether `filter` reads nothing but run names and has-metric checks. */
function readsOnlyRunNames(filter: FilterNode<Key>): boolean {
  return Filter.getUniqueKeysUsedInFilter(filter).every(
    key =>
      (key.section === 'run' && key.name === 'name') ||
      key.section === 'keys_info'
  );
}

/**
 * `data` without the runs `request` does not select, or null when that
 * cannot be told from run names alone: grouped lines are groups, pinned runs
 * come back whatever the filters say, and other filters read fields a line
 * does not carry. Has-metric (keys_info) filters pass every run: the data
 * comes from a request for the same metrics.
 */
export function narrowToSelection(
  data: BucketedData,
  request: QueryStoreRequest
): BucketedData | null {
  const {filters, pinnedRunIds, selections} = request.singleQuery;
  const selecting = selections ? [filters, selections] : [filters];
  if (
    isGroupedQueryType(request.queryType) ||
    (pinnedRunIds?.length ?? 0) > 0 ||
    !selecting.every(readsOnlyRunNames)
  ) {
    return null;
  }
  const runsById = Object.fromEntries(
    Object.entries(data.runsById).filter(([, line]) =>
      selecting.every(filter =>
        Filter.match(filter, {name: line.name} as Run.RunWithSupportedKeys)
      )
    )
  );
  return {...data, order: data.order.filter(id => id in runsById), runsById};
}

/** The candidate whose zoom is contained by every other's. */
function tightest(
  candidates: PlaceholderCandidate[]
): PlaceholderCandidate | undefined {
  return [...candidates].sort((a, b) =>
    isZoomContained(a.request.runsDataQuery, b.request.runsDataQuery) ? -1 : 1
  )[0];
}

/**
 * The best settled data to show while `target` loads:
 * 1. The same request with the most buckets that is not more than its own.
 * 2. The same request zoomed out, tightest first.
 * 3. A different selection of runs zoomed out, tightest first, narrowed to
 *    the runs `target` selects when run names can tell. Otherwise it is
 *    shown as it is: the previous selection's lines for a moment beat a
 *    blank panel after a run toggle.
 */
export function findPlaceholder(
  candidates: PlaceholderCandidate[],
  target: {keys: RequestKeys; request: QueryStoreRequest}
): BucketedData | null {
  const contains = (candidate: PlaceholderCandidate) =>
    isZoomContained(
      target.request.runsDataQuery,
      candidate.request.runsDataQuery
    );

  const fewerBuckets = candidates
    .filter(
      c =>
        c.keys.fidelity === target.keys.fidelity &&
        c.request.nBuckets <= target.request.nBuckets
    )
    .sort((a, b) => b.request.nBuckets - a.request.nBuckets)[0];
  if (fewerBuckets) {
    return fewerBuckets.data;
  }

  const zoomedOut = tightest(
    candidates.filter(c => c.keys.family === target.keys.family && contains(c))
  );
  if (zoomedOut) {
    return zoomedOut.data;
  }

  const reselected = tightest(
    candidates.filter(
      c => c.keys.selection === target.keys.selection && contains(c)
    )
  );
  if (reselected) {
    return (
      narrowToSelection(reselected.data, target.request) ?? reselected.data
    );
  }
  return null;
}
